import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ClientGroup } from '@/types/client';
import { createClientGroup, updateClientGroup, deleteClientGroup } from '@/services/client.service';
import { toastService } from '@/services/toast.service';
import { useClientOptions } from '@/hooks/useClientOptions';

export const ClientGroupsManager: React.FC = () => {
  const { groups, refetch } = useClientOptions();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ClientGroup | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2196f3');
  const [loading, setLoading] = useState(false);

  const handleOpenDialog = (group?: ClientGroup) => {
    if (group) {
      setEditingGroup(group);
      setName(group.name);
      setColor(group.color);
    } else {
      setEditingGroup(null);
      setName('');
      setColor('#2196f3');
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingGroup(null);
    setName('');
    setColor('#2196f3');
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      if (editingGroup) {
        await updateClientGroup(editingGroup.id, { name, color });
        toastService.success('Групу оновлено');
      } else {
        await createClientGroup({ name, color });
        toastService.success('Групу створено');
      }
      refetch();
      handleCloseDialog();
    } catch (err) {
      toastService.error('Помилка при збереженні групи');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Ви впевнені, що хочете видалити цю групу?')) return;
    try {
      await deleteClientGroup(id);
      toastService.success('Групу видалено');
      refetch();
    } catch (err) {
      toastService.error('Помилка при видаленні групи');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Управління групами</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={() => handleOpenDialog()}
        >
          Додати групу
        </Button>
      </Box>

      <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
        {groups.map((group) => (
          <ListItem key={group.id} divider>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: group.color,
                mr: 2,
              }}
            />
            <ListItemText
              primary={group.name}
              secondary={`${group.clientsCount || 0} клієнтів`}
            />
            <ListItemSecondaryAction>
              <IconButton edge="end" onClick={() => handleOpenDialog(group)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton edge="end" onClick={() => handleDelete(group.id)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {groups.length === 0 && (
          <ListItem>
            <ListItemText primary="Груп ще не створено" />
          </ListItem>
        )}
      </List>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="xs">
        <DialogTitle>{editingGroup ? 'Редагувати групу' : 'Створити групу'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Назва групи"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            sx={{ mb: 2, mt: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Колір:</Typography>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: '100%', height: '40px', cursor: 'pointer', border: 'none', padding: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !name.trim()}>
            {editingGroup ? 'Оновити' : 'Створити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
